// RelatedServiceItem.jsx
import React from "react";
import BookmarkButton from "../service/BookmarkButton.jsx";
import { PriceDisplay } from "../common/PriceDisplay.jsx";
import NewBadge from "../common/NewBadge.jsx";

export function RelatedServiceItem({ item, onClick, isActive = false }) {
  const title = item?.profile?.name ?? "Untitled";
  const image = item?.profile?.image ?? null;

  const category = item?.profile?.category ?? "Uncategorized";
  const subCategory = item?.profile?.sub_category ?? null;

  const tags = Array.isArray(item?.service_location?.tags) ? item.service_location.tags : [];
  const ratingAvg = item?.service_review?.ratings_average;
  const ratingCount = item?.service_review?.ratings_count ?? 0;

  const price = item?.display_price?.price;
  const discountType = item?.display_price?.discount_type;
  const discountValue = item?.display_price?.discount_value;

  return (
    // 카드 안에 북마크 <button> 이 있어 카드 자신은 button 이면 안 된다
    // (버튼 중첩은 invalid HTML — React 가 hydration error 를 뿜는다).
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.(e);
        }
      }}
      aria-current={isActive ? "page" : undefined}
      className={[
        "min-w-0 w-full text-left rounded-2xl border bg-white overflow-hidden transition cursor-pointer",
        "border-border-tertiary hover:shadow-sm",
      ].join(" ")}
    >
      <div className="relative w-full aspect-[3/2] bg-bg-secondary overflow-hidden">
        {image ? (
          <img
            src={image}
            alt={title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover block"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-text-tertiary text-body-md">
            Image
          </div>
        )}

        {/* 좌상단은 상태 표식 자리 — 우상단은 북마크가 쓴다. 둘은 공존한다. */}
        <div className="absolute top-2 left-2 flex items-center gap-1">
          {isActive ? (
            <div className="px-2 py-1 rounded-full text-xs bg-white/90 border border-border-tertiary">
              Selected
            </div>
          ) : null}
          {item?.is_new ? <NewBadge /> : null}
        </div>

        <div className="absolute top-2 right-2">
          <BookmarkButton uuid={item?.uuid} transparent />
        </div>
      </div>

      <div className="p-4">
        <div className="pb-2">
          <div className="text-body-lg-sb text-text-primary line-clamp-2 min-h-[3em]">
            {/* 카드는 div+onClick 이라 크롤러가 따라갈 링크가 없었다 → 제목을 실제 <a href>로.
                일반 클릭은 카드 onClick(SPA 이동)에 맡기고, 새 탭 클릭만 브라우저에 넘긴다. */}
            {item?.uuid ? (
              <a
                href={`/service/${item.uuid}`}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey) e.stopPropagation();
                  else e.preventDefault();
                }}
              >
                {title}
              </a>
            ) : (
              title
            )}
          </div>
        </div>
        <div className="text-body-sm text-text-secondary line-clamp-2">
          {category} • {subCategory}
        </div>

        {tags.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1">
            {tags.slice(0, 3).map((t, idx) => (
              <span
                key={`${t}-${idx}`}
                className="px-2 py-0.5 rounded-full bg-bg-secondary border border-border-tertiary text-body-sm text-text-tertiary"
              >
                {t}
              </span>
            ))}
          </div>
        ) : null}

        <div className="mt-3 flex flex-col items-start gap-1">
          <div className="text-body-sm text-text-tertiary">
            {ratingCount > 0 ? (
              <>
                <span className="text-icon-accentOrange">★</span> {ratingAvg.toFixed(1)} ({ratingCount})
              </>
            ) : (
              ""
            )}
          </div>

          <div className="text-body-md-sb text-text-primary text-left">
            {price != null ? (
              <>
                <span className="text-body-sm text-text-tertiary font-normal">From </span>
                <PriceDisplay price={price} />
                <span className="text-body-sm text-text-tertiary font-normal"> ~</span>
              </>
            ) : null}
            {discountType && discountValue != null ? (
              <span className="ml-2 text-body-sm text-text-tertiary">
                {discountType === "PERCENTAGE"
                  ? `(-${discountValue}%)`
                  : <span>(-<PriceDisplay price={discountValue} />)</span>}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
